import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, authHandlers, formatApiError } from "./api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const userRef = useRef(null);
  const notifiedRef = useRef(false);
  // Suppress the "session expired" toast for a short window right after login
  // and after the initial mount to avoid flashes caused by in-flight requests.
  const suppressUntilRef = useRef(Date.now() + 4000);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    let mounted = true;
    let loginHappened = false;

    authHandlers.onUnauthorized = ({ silent }) => {
      if (silent) return;
      if (Date.now() < suppressUntilRef.current) return; // grace period
      if (!userRef.current) return;
      if (notifiedRef.current) return;
      notifiedRef.current = true;
      setTimeout(() => { notifiedRef.current = false; }, 3000);
      toast.error("Your session has expired. Please sign in again.");
      setUser(false);
    };

    // Expose so login() can tell the initial /me not to clobber it.
    authHandlers.markLoginHappened = () => { loginHappened = true; };

    api
      .get("/auth/me")
      .then((r) => { if (mounted && !loginHappened) setUser(r.data); })
      .catch(() => { if (mounted && !loginHappened && !userRef.current) setUser(false); })
      .finally(() => { if (mounted) setChecking(false); });

    return () => {
      mounted = false;
      authHandlers.onUnauthorized = null;
      authHandlers.markLoginHappened = null;
    };
  }, []);

  async function login(email, password) {
    try {
      const r = await api.post("/auth/login", { email, password });
      // Extend the grace window so any late /me or admin call doesn't toast.
      suppressUntilRef.current = Date.now() + 4000;
      if (authHandlers.markLoginHappened) authHandlers.markLoginHappened();
      setUser(r.data);
      userRef.current = r.data;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    userRef.current = null;
  }

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
